import { useEffect, useState, type JSX } from 'react';
import {
  SettingsDrawer,
  Toggle,
  Collapsible,
  accountSection,
  securitySection,
  type AdminSection,
} from '@cca/ui';
import { PasskeysCard } from './PasskeysCard';
import { NzpvsFillerInstall } from './nzpvs-filler/NzpvsFillerInstall';
import { signOutAllDevices } from '../auth/signout';

/**
 * Vetting Portal Settings — the estate-standard role-aware drawer (replaces the
 * header "Security" popover and the inline lodgement-form toggle). Mounted on the
 * registrar dashboard, so the viewer is always the registrar (superuser).
 * Sign-out (this device / all devices) stays on the header SignOutMenu.
 */
export function VettingSettings({
  open,
  onClose,
}: {
  open: boolean;
  onClose: () => void;
}): JSX.Element {
  const [email, setEmail] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    fetch('/api/vetting/auth/me', { credentials: 'include' })
      .then((r) => (r.ok ? r.json() : null))
      .then((d: { email?: string } | null) => d?.email && setEmail(d.email))
      .catch(() => {});
  }, [open]);

  const sections: AdminSection[] = [
    accountSection({
      identity: email ? (
        <>
          Signed in as <strong style={{ color: 'var(--color-ink, #111827)' }}>{email}</strong>.
        </>
      ) : (
        'Signed in.'
      ),
      onSignOutThisDevice: () => {
        window.location.href = '/auth/logout';
      },
      onSignOutAll: signOutAllDevices,
    }),
    securitySection({
      title: 'Security',
      minRole: 'superuser',
      // Collapsed by default — passkey management likely belongs elsewhere
      // (a per-user account surface, not Vetting-specific); see bead cca-wst.
      passkeys: (
        <Collapsible title="Passkeys — sign in without a code">
          <PasskeysCard hideTitle />
        </Collapsible>
      ),
    }),
    {
      key: 'lodgement',
      title: 'Lodgement form',
      minRole: 'superuser',
      body: <LodgementToggle />,
    },
    // cca-uzt5: installs the CCAANZ → NZPVS filler bookmarklet. A tool, so it
    // lives here and not on a work page.
    {
      key: 'nzpvs-filler',
      title: 'NZPVS filler',
      minRole: 'superuser',
      body: <NzpvsFillerInstall />,
    },
  ];

  // Registrar-gated page → the viewer is always the registrar (superuser). The
  // drawer heading rule falls back to the portal's --color-structural (emerald).
  return <SettingsDrawer open={open} onClose={onClose} viewer="superuser" sections={sections} />;
}

/**
 * The interim lodgement-form choice (official NZ Police form vs the CCAANZ
 * bespoke form), modelled as a switch: on = bespoke, off = official. Optimistic +
 * revert-on-error via the shared Toggle.
 */
function LodgementToggle(): JSX.Element {
  const [choice, setChoice] = useState<'official' | 'cca' | null>(null);

  useEffect(() => {
    fetch('/api/vetting/registrar/settings/lodgement-form', { credentials: 'include' })
      .then((r) => (r.ok ? r.json() : null))
      .then((d: { choice?: 'official' | 'cca' } | null) => d?.choice && setChoice(d.choice))
      .catch(() => {});
  }, []);

  if (choice === null) {
    return <p style={{ fontSize: 13, color: 'var(--color-muted, #6b7280)' }}>Loading…</p>;
  }

  return (
    <Toggle
      checked={choice === 'cca'}
      label="Use the CCAANZ bespoke form"
      description="On = CCAANZ bespoke form; off = the NZ Police official form. Applies to approvals from now on — both PDFs are always saved to Drive."
      onChange={async (next) => {
        const value = next ? 'cca' : 'official';
        const res = await fetch('/api/vetting/registrar/settings/lodgement-form', {
          method: 'PUT',
          credentials: 'include',
          headers: { 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
          body: JSON.stringify({ choice: value }),
        });
        if (!res.ok) throw new Error('Could not save the lodgement-form choice.');
        setChoice(value);
      }}
    />
  );
}
